import type { ApiErrorBody, TokenResponse } from './types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const ACCESS_TOKEN_KEY = 'catering.accessToken'
export const ACTIVE_COMPANY_KEY = 'catering.activeCompany'
/** The branch being worked in. Absent means every one the person may reach. */
export const ACTIVE_OUTLET_KEY = 'catering.activeOutlet'
const REFRESH_TOKEN_KEY = 'catering.refreshToken'

/**
 * What to say when the failure arrived with no words of its own.
 *
 * <p>A banner with nothing in it is worse than no banner: the reader knows
 * something went wrong and cannot tell what, or whether their own next move
 * should be to retry, to sign in again, or to call somebody.</p>
 *
 * <p>The gap is not rare. Anything that fails before it reaches the API - the
 * gateway while the API restarts, a connection dropped mid-flight - answers
 * without our JSON error body, and HTTP/2 carries no reason phrase, so there
 * is no status text to fall back on either.</p>
 */
function describe(status: number): string {
  if (status === 401) return 'Your session has ended. Sign in again.'
  if (status === 403) return 'You are not allowed to do that.'
  if (status === 404) return 'That is no longer there.'
  if (status >= 500) return 'The server did not answer — it may be restarting. Try again in a moment.'
  return `Request failed with status ${status}`
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: { field: string; message: string }[]
  readonly details: Record<string, unknown>

  constructor(status: number, body: Partial<ApiErrorBody>) {
    // Blank counts as absent. `??` alone lets an empty message through, and an
    // empty message is exactly what a gateway failure hands us.
    super(body.message?.trim() || describe(status))
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? 'UNKNOWN'
    this.fieldErrors = (body.fieldErrors ?? []).map((error) => ({
      field: error.field,
      message: error.message,
    }))
    this.details = body.details ?? {}
  }

  /** True when the server is telling us the action needs someone else's sign-off. */
  get needsApproval() {
    return this.code === 'APPROVAL_REQUIRED'
  }

  /** True when the record changed under us and the screen should reload. */
  get isStale() {
    return this.code === 'STALE_RECORD'
  }
}

export const tokenStorage = {
  access: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  refresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  save(tokens: Pick<TokenResponse, 'accessToken' | 'refreshToken'>) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  params?: Record<string, string | number | boolean | undefined | null>
  /** Sent as the Idempotency-Key header; used for payments and confirmations. */
  idempotencyKey?: string
  signal?: AbortSignal
  /** Skips the bearer token, for the public customer links. */
  anonymous?: boolean
}

function buildUrl(path: string, params?: RequestOptions['params']) {
  const url = `${BASE_URL}${path}`
  if (!params) return url
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value))
    }
  })
  const queryString = query.toString()
  return queryString ? `${url}?${queryString}` : url
}

/** Serialises refresh attempts so a burst of 401s triggers only one refresh. */
let refreshInFlight: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokenStorage.refresh()
  if (!refreshToken) return false

  if (!refreshInFlight) {
    refreshInFlight = fetch(buildUrl('/api/v1/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          tokenStorage.clear()
          return false
        }
        const tokens = (await response.json()) as TokenResponse
        tokenStorage.save(tokens)
        return true
      })
      .catch(() => {
        tokenStorage.clear()
        return false
      })
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody
    return new ApiError(response.status, body)
  } catch {
    // statusText is empty on every HTTP/2 response, which is everything Caddy
    // serves; the constructor describes the status when it is.
    return new ApiError(response.status, { message: response.statusText })
  }
}

export const COMPANY_HEADER = 'X-Company-Id'

/**
 * The company the user has switched to, if any.
 *
 * <p>Read from storage rather than imported from the auth store, because the
 * store imports this module and the cycle would leave one of them undefined at
 * load time.</p>
 */
function activeCompany(): string | null {
  return localStorage.getItem(ACTIVE_COMPANY_KEY)
}

async function send<T>(path: string, options: RequestOptions, retrying = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  const token = tokenStorage.access()
  if (token && !options.anonymous) headers.Authorization = `Bearer ${token}`

  // Which business the screen is showing. Everything the request touches
  // belongs to it - reads, writes and totals alike - so it travels with every
  // call rather than being remembered on the server, where a second tab would
  // quietly change what the first one was looking at.
  const company = activeCompany()
  if (company && !options.anonymous) headers[COMPANY_HEADER] = company

  const response = await fetch(buildUrl(path, options.params), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 401 && !options.anonymous && !retrying) {
    const refreshed = await refreshAccessToken()
    if (refreshed) return send<T>(path, options, true)
    tokenStorage.clear()
    window.dispatchEvent(new CustomEvent('catering:signed-out'))
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('application/json')) return (await response.text()) as T
  return (await response.json()) as T
}

/**
 * Sends a file.
 *
 * <p>Kept apart from {@link send} because the browser has to set the
 * Content-Type itself: a multipart body needs a boundary, and naming the type
 * by hand loses it and the server sees nothing.</p>
 */
export async function uploadForm<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = tokenStorage.access()
  if (token) headers.Authorization = `Bearer ${token}`
  const company = activeCompany()
  if (company) headers[COMPANY_HEADER] = company

  const response = await fetch(buildUrl(path), { method: 'POST', headers, body })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

/** A customer adding a file to their own feedback link: no sign-in, so nothing is sent but the file. */
export async function publicUpload<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(buildUrl(path), { method: 'POST', headers: { Accept: 'application/json' }, body })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params'], signal?: AbortSignal) =>
    send<T>(path, { method: 'GET', params, signal }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, params?: RequestOptions['params']) =>
    send<T>(path, { method: 'DELETE', params }),

  /** For the public customer links, which authenticate with a token in the path. */
  publicGet: <T>(path: string, params?: RequestOptions['params']) =>
    send<T>(path, { method: 'GET', params, anonymous: true }),

  publicPost: <T>(path: string, body?: unknown) =>
    send<T>(path, { method: 'POST', body, anonymous: true }),

  /**
   * Fetches a stored file as a blob url, for showing it on the page rather
   * than saving it. The caller revokes the url when it is done with it.
   *
   * <p>Needed because a document is behind the bearer token, and an img tag
   * pointed straight at the endpoint sends no headers and gets a 401.</p>
   */
  async blobUrl(path: string) {
    const token = tokenStorage.access()
    const company = activeCompany()
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    if (company) headers[COMPANY_HEADER] = company
    const response = await fetch(buildUrl(path), { headers })
    if (!response.ok) throw await parseError(response)
    return URL.createObjectURL(await response.blob())
  },

  /**
   * Opens a document in the browser's print dialog.
   *
   * <p>Fetched with the bearer token and handed to a hidden frame, because a
   * window pointed straight at the endpoint sends no headers and gets a 401.
   * The frame is left in place until the dialog is done with it - removing it
   * on the next line cancels the print.</p>
   *
   * <p>Says it is a print, so the office's own count of what left the building
   * can tell a print from a file somebody saved.</p>
   */
  async print(path: string, params?: RequestOptions['params']) {
    const token = tokenStorage.access()
    const company = activeCompany()
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    if (company) headers[COMPANY_HEADER] = company
    const response = await fetch(buildUrl(path, { ...(params ?? {}), as: 'print' }), { headers })
    if (!response.ok) throw await parseError(response)

    const url = URL.createObjectURL(await response.blob())
    const frame = document.createElement('iframe')
    frame.style.position = 'fixed'
    frame.style.right = '0'
    frame.style.bottom = '0'
    frame.style.width = '0'
    frame.style.height = '0'
    frame.style.border = '0'
    frame.src = url
    frame.onload = () => {
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
    }
    document.body.appendChild(frame)
    // Long enough for the dialog to have taken the document. The url is the
    // only thing worth reclaiming; the frame is empty and costs nothing.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },

  /** Streams a file download (Excel report, quotation PDF) with auth applied. */
  async download(path: string, params?: RequestOptions['params'], fallbackName = 'download') {
    const token = tokenStorage.access()
    const company = activeCompany()
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    // A report downloaded while looking at one business must be that
    // business's, not the home company's.
    if (company) headers[COMPANY_HEADER] = company
    // Said the same way a print says it, so the count can tell the two apart.
    const response = await fetch(buildUrl(path, { ...(params ?? {}), as: 'download' }), { headers })
    if (!response.ok) throw await parseError(response)

    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename="?([^"]+)"?/.exec(disposition)
    const blob = await response.blob()

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = match?.[1] ?? fallbackName
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  },
}
